import { Link } from 'react-router-dom';
import {
  FlaskConical,
  Zap,
  Users,
  ArrowRight,
  ExternalLink,
  Sparkles,
  Globe,
  Swords,
  GraduationCap,
  Code2,
} from 'lucide-react';
import { TrinetraLoop3 } from '@/components/icons/trinetra-icons';

export function LabsPage() {
  return (
    /* `dark` class forces dark-mode tokens on this branded launch page
     * regardless of user theme — design is intentionally a deep-bg dark
     * aesthetic (glows, white heading, blue accents). Scoping to this
     * subtree leaves the rest of the app theme-aware. */
    <div className="dark min-h-screen bg-gray-950 text-white overflow-hidden">

      {/* ─── Ambient Background ─── */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-purple-600/5 rounded-full blur-[100px]" />
      </div>

      {/* ─── Nav ─── */}
      <nav className="relative z-10 flex items-center justify-between px-6 py-5 max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors text-sm">
          <FlaskConical className="h-5 w-5" />
          <span>Nirvik AI Labs</span>
        </Link>
        <a
          href="mailto:labs@nirvk.ai"
          className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
        >
          Get in Touch <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative z-10 px-6 pt-20 pb-32 max-w-4xl mx-auto text-center">
        {/* Date badge */}
        <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-4 py-1.5 rounded-full text-sm font-medium mb-10">
          <Sparkles className="h-4 w-4" />
          April 4, 2026 — Founding Day
        </div>

        {/* Lab icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-2xl shadow-blue-500/20">
              <FlaskConical className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -inset-3 rounded-3xl bg-blue-500/10 blur-xl -z-10 animate-pulse" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-6xl md:text-8xl font-black tracking-tight mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent leading-none">
          Nirvik AI Labs
        </h1>

        <p className="text-xl md:text-2xl font-medium text-foreground mb-4 tracking-wide">
          Building the Next Generation of Cyber Defenders
        </p>

        <div className="h-px w-24 bg-gradient-to-r from-transparent via-blue-500 to-transparent mx-auto mt-10" />
      </section>

      {/* ─── The Moment ─── */}
      <section className="relative z-10 px-6 py-24 max-w-3xl mx-auto">
        <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400 mb-8 text-center">
          The Inflection Point
        </h2>

        <div className="space-y-8 text-lg md:text-xl leading-relaxed text-foreground">
          <p>
            Today, Anthropic unveiled{' '}
            <a
              href="https://red.anthropic.com/2026/mythos-preview/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline underline-offset-4 decoration-blue-500/30 inline-flex items-center gap-1"
            >
              Project GlassWing and Claude Mythos
              <ExternalLink className="h-4 w-4" />
            </a>
            {' '}— a model that autonomously discovers zero-day vulnerabilities hidden for decades, builds multi-stage exploit chains, and breaches systems that withstood years of manual review and fuzzing.
          </p>

          <p>
            A 27-year-old vulnerability in OpenBSD. A 17-year-old remote root exploit in FreeBSD. Over 1,000 critical vulnerabilities found across every major operating system and browser.
          </p>

          <p className="text-white font-medium text-xl md:text-2xl py-4">
            The era of AI-native cybersecurity is here. Not in five years. Today.
          </p>

          <p>
            The question is no longer <em>whether</em> AI will transform cybersecurity. The question is: who will wield it — attackers or defenders?
          </p>
        </div>

        <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-blue-950/50 to-cyan-950/30 border border-blue-500/20">
          <p className="text-2xl md:text-3xl font-bold text-white text-center leading-snug">
            We choose to build defenders.
          </p>
        </div>
      </section>

      {/* ─── Divider ─── */}
      <div className="flex justify-center py-8">
        <div className="flex items-center gap-3">
          <div className="h-px w-16 bg-muted" />
          <FlaskConical className="h-5 w-5 text-gray-700" />
          <div className="h-px w-16 bg-muted" />
        </div>
      </div>

      {/* ─── Mission ─── */}
      <section className="relative z-10 px-6 py-24 max-w-5xl mx-auto">
        <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 text-center">
          Our Mission
        </h2>
        <p className="text-3xl md:text-4xl font-bold text-center text-white mb-6 max-w-3xl mx-auto leading-snug">
          Making advanced cybersecurity accessible to every builder on the planet
        </p>
        <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-16">
          The next billion lines of code will be written by builders who aren't security experts. We're making sure that doesn't matter.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Zap,
              title: 'AI-Powered Defense',
              description: 'Harness frontier AI models to find and fix vulnerabilities before attackers do. Autonomous scanning, intelligent triage, and plain-language remediation.',
              color: 'from-blue-600 to-blue-500',
              glow: 'bg-blue-500/10',
            },
            {
              icon: Users,
              title: 'Democratize Security',
              description: 'Translate complex security concepts into language anyone can understand. Every vulnerability explained. Every fix actionable. No expertise required.',
              color: 'from-cyan-500 to-teal-500',
              glow: 'bg-cyan-500/10',
            },
            {
              icon: Globe,
              title: 'Open Research',
              description: 'Advance the science of automated vulnerability discovery and responsible disclosure. Publish findings. Share tools. Strengthen the ecosystem.',
              color: 'from-purple-600 to-indigo-500',
              glow: 'bg-purple-500/10',
            },
          ].map((pillar) => (
            <div
              key={pillar.title}
              className="relative group p-8 rounded-2xl bg-card border border-border hover:border-border transition-all hover:-translate-y-1"
            >
              <div className={`absolute inset-0 rounded-2xl ${pillar.glow} opacity-0 group-hover:opacity-100 transition-opacity blur-xl -z-10`} />
              <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-5 shadow-lg`}>
                <pillar.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">{pillar.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── What We're Building ─── */}
      <section className="relative z-10 px-6 py-24 max-w-5xl mx-auto">
        <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 text-center">
          What We're Building
        </h2>
        <p className="text-3xl md:text-4xl font-bold text-center text-white mb-16 max-w-3xl mx-auto leading-snug">
          From research to reality
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              icon: TrinetraLoop3,
              title: 'Trinetra',
              description: 'Our flagship platform — AI-powered security scanning that finds vulnerabilities across code, dependencies, infrastructure, and cloud configurations. 23 specialized agents working in concert.',
              tag: 'Platform',
              link: '/',
            },
            {
              icon: Swords,
              title: 'AI Red Team Engine',
              description: 'Autonomous security testing powered by frontier models. Simulates real-world attack chains, discovers exploit paths, and validates defenses — continuously.',
              tag: 'Research',
            },
            {
              icon: GraduationCap,
              title: 'Security Education',
              description: 'Plain-language security learning for the next million developers. Every vulnerability explained. Every concept demystified. Because security literacy scales better than security teams.',
              tag: 'Education',
            },
            {
              icon: Code2,
              title: 'Open Source Tools',
              description: 'Contributing back to the security community. Open-source scanners, detection rules, and AI-powered remediation tools that anyone can use and extend.',
              tag: 'Open Source',
            },
          ].map((item) => (
            <div
              key={item.title}
              className="relative group p-8 rounded-2xl bg-card border border-border hover:border-border transition-all"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="h-12 w-12 rounded-xl bg-muted border border-border flex items-center justify-center group-hover:border-blue-500/30 transition-colors">
                  <item.icon className="h-6 w-6 text-blue-400" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">
                  {item.tag}
                </span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{item.description}</p>
              {item.link && (
                <Link
                  to={item.link}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Try Trinetra <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ─── Divider ─── */}
      <div className="flex justify-center py-8">
        <div className="flex items-center gap-3">
          <div className="h-px w-16 bg-muted" />
          <Sparkles className="h-5 w-5 text-gray-700" />
          <div className="h-px w-16 bg-muted" />
        </div>
      </div>

      {/* ─── The Era ─── */}
      <section className="relative z-10 px-6 py-32 max-w-3xl mx-auto text-center">
        <div className="mb-12">
          <blockquote className="text-xl md:text-2xl font-medium text-foreground italic leading-relaxed">
            &ldquo;In the long run, we expect that defense capabilities will dominate&hellip; the world will emerge more secure, with software better hardened.&rdquo;
          </blockquote>
          <cite className="block mt-4 text-sm text-muted-foreground not-italic">
            — Anthropic, Project GlassWing
          </cite>
        </div>

        <p className="text-2xl md:text-3xl font-bold text-white">
          We're building for that future.{' '}
          <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Today.
          </span>
        </p>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative z-10 px-6 py-24 bg-gradient-to-b from-transparent via-blue-950/20 to-gray-950">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join the Mission
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            Whether you're a security researcher, a builder, or someone who believes software should be safer — there's a place for you here.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:labs@nirvk.ai"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3.5 rounded-xl text-lg font-semibold transition-all hover:shadow-xl hover:shadow-blue-500/20 hover:-translate-y-0.5"
            >
              Get in Touch
              <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-muted hover:bg-muted text-foreground px-8 py-3.5 rounded-xl text-lg font-semibold transition-all border border-border hover:border-border"
            >
              Visit Nirvik
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="relative z-10 px-6 py-12 border-t border-border/50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <FlaskConical className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Nirvik AI Labs</span>
          </div>
          <p className="text-sm text-muted-foreground mb-2">Founded April 4, 2026</p>
          <p className="text-xs text-muted-foreground">A new era of cyber defense begins.</p>
        </div>
      </footer>
    </div>
  );
}
